import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskMainLayoutConfig } from '@front-platform/content-builder';
import { kioskMainLayoutResolveConfig } from '@front-platform/content-builder';
import { Component, effect, inject, input, computed } from '@angular/core';
import { KioskGlobalSettings } from '@core/models/kiosk-global-settings.model';
import { AnonymousAuthenticationService } from '@core/services/auth/anonymous-authentification.service';
import { KioskService } from '@core/services/kiosk/kiosk.service';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';

function readRawKioskGlobalSettings(
  config: object | null | undefined,
): Partial<KioskGlobalSettings> | undefined {
  if (!config) {
    return undefined;
  }
  const record = config as Record<string, unknown>;
  const raw = record['kiosk_global_settings'] ?? record['kioskGlobalSettings'];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  return raw as Partial<KioskGlobalSettings>;
}

export interface KioskMainLayoutRuntimeConfig {
  'footer'?: unknown[];
  'global_features'?: unknown[];
}

export type KioskMainLayoutConfigInput = Partial<
  Omit<KioskMainLayoutConfig, keyof KioskMainLayoutRuntimeConfig> & KioskMainLayoutRuntimeConfig
>;
export type KioskMainLayoutResolvedConfig = Omit<
  KioskMainLayoutConfig,
  keyof KioskMainLayoutRuntimeConfig
> &
  Partial<KioskMainLayoutRuntimeConfig>;
export interface KioskMainLayoutContract extends ConfigComponentContract<
  KioskMainLayoutConfigInput,
  KioskMainLayoutResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-main-layout',
  templateUrl: './kiosk-main-layout.component.html',
  standalone: false,
})
export class KioskMainLayoutComponent implements KioskMainLayoutContract {
  readonly config = input<KioskMainLayoutConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskMainLayoutConfig,
    KioskMainLayoutRuntimeConfig
  >(kioskMainLayoutResolveConfig, () => ({ config: this.config() }));
  private readonly kioskService = inject(KioskService);
  private readonly anonymousAuthenticationService = inject(AnonymousAuthenticationService);
  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);

  readonly footer = computed(() => this.resolvedConfig()['footer'] as unknown as unknown[]);
  readonly globalFeatures = computed(
    () => this.resolvedConfig()['global_features'] as unknown as unknown[],
  );
  // Raw block data, not the resolved config: the resolver always injects a
  // default `productCard`, which would hide a stored `product_card` sibling.
  readonly kioskGlobalSettings = computed(() => readRawKioskGlobalSettings(this.config()));

  constructor() {
    this.kioskService.saveKioskLayoutToStore(true);
    this.anonymousAuthenticationService.ensureAnonymousSession$().subscribe();
    effect(() => {
      this.kioskGlobalSettingsService.loadSettings(this.kioskGlobalSettings());
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskMainLayoutComponent, { 'keyAliases': {} });
